import { cn } from '@brew/ui/cn'
import type { ReactNode } from 'react'
import { LocalizedLink } from '@/components/i18n/localized-link'
import { AuthModalLink } from '../v2/auth-modal-link'
import { isExternalHref } from './external-href'
import { HOME_LINKS } from './home-links'

export type HomeButtonVariant = 'brand' | 'light' | 'outline'
export type HomeButtonSize = 'sm' | 'md'

const BASE =
  'flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap font-medium font-mk-body tracking-[-0.01em] transition-[filter,background-color,box-shadow] duration-150 focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2'

const VARIANT: Record<HomeButtonVariant, string> = {
  /** Orange gradient pill — every primary CTA on the page and in the nav. */
  brand:
    'rounded-[70px] border-[1.5px] border-mk-brand-border bg-mk-brand text-white shadow-mk-button hover:brightness-105',
  /** White pill on the night band. Fixed colors: the band is dark in both themes. */
  light:
    'rounded-full border border-[#f7f7f547] bg-white text-[#1e1e1e] hover:bg-white/90',
  /** Ringed card-colored pill ("Browse all templates →"). */
  outline:
    'rounded-[70px] bg-mk-card text-mk-ink shadow-[0_0_0_1px_var(--mk-ring),var(--mk-button-shadow)] hover:bg-mk-tile',
}

/**
 * Every button's label is the same 14px medium; only the height and padding
 * step. `sm` (36px) for the navbar and small cards, `md` (40px, the other
 * marketing pages' `PillButton` height) for every other CTA and the hero
 * field's submit. Heights are pinned rather than derived from padding: at
 * 1× DPR Chrome floors the brand pill's 1.5px border to 1px, which would
 * make padded brand buttons 1px shorter than the others.
 */
const SIZE: Record<HomeButtonSize, string> = {
  sm: 'h-9 px-4 text-[14px] leading-5',
  md: 'h-10 px-5 text-[14px] leading-5',
}

/**
 * Class string for a home CTA, for elements `HomeButton` cannot render
 * (a form's submit `<button>`, Clerk's `SignUpButton` child).
 */
export function homeButtonClasses({
  variant = 'brand',
  size = 'md',
  className,
}: {
  variant?: HomeButtonVariant
  size?: HomeButtonSize
  className?: string
} = {}): string {
  return cn(BASE, VARIANT[variant], SIZE[size], className)
}

export type HomeButtonProps = {
  /** A site path without a locale (`/pricing`), or an external URL. */
  href: string
  children: ReactNode
  variant?: HomeButtonVariant
  size?: HomeButtonSize
  className?: string
}

/**
 * A home-page CTA link. External hrefs open in a new tab. A site path is
 * given without a locale (`/pricing`, `/sign-up`) and links to the reader's
 * language: `LocalizedLink` and `AuthModalLink` add their locale.
 */
export function HomeButton({
  href,
  children,
  variant,
  size,
  className,
}: HomeButtonProps) {
  const classes = homeButtonClasses({ variant, size, className })

  // "Start free" and the like open the auth modal in place.
  if (href === HOME_LINKS.signUp) {
    return (
      <AuthModalLink href={href} className={classes}>
        {children}
      </AuthModalLink>
    )
  }

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
      >
        {children}
      </a>
    )
  }

  return (
    <LocalizedLink href={href} prefetch={true} className={classes}>
      {children}
    </LocalizedLink>
  )
}
